import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Chip,
	createStyles,
	Flex,
	Grid,
	Group,
	Loader,
	NumberInput,
	Popover,
	Progress,
	ScrollArea,
	Select,
	Stack,
	Text,
	Tooltip,
	useMantineTheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFilter } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { makeSafeRequest } from '../../common/makeSafeRequest';

interface ISimulationPage {}

type SimulationProgressResponse = {
	progress: SimulationProgress[];
};

type SimulationProgress = {
	runnum: number;
	status: string;
	progress: number;
};

type ScenarioOption = {
	id: number;
	name: string;
	project_name: string | null;
};

type ScenariosResponse = {
	scenarios: ScenarioOption[];
};

const statusColor = (status: string) => {
	switch (status.toLowerCase()) {
		case 'running':
			return 'blue';
		case 'finished':
			return 'green';
		case 'failed':
			return 'red';
		case 'pending':
			return 'yellow';
		default:
			return 'gray';
	}
};

const SimulationPage: React.FC<ISimulationPage> = () => {
	const { classes } = useStyles();
	const location: { state: { scenarioId?: string } } = useLocation();
	const theme = useMantineTheme();

	const [scenarioId, setScenarioId] = useState(location?.state?.scenarioId || '');
	const [duration, setDuration] = useState<number>(12);
	const [memValue, setMemValue] = useState<number>(25);
	const [memUnit, setMemUnit] = useState<string>('Gi');
	const [cpuLimit, setCpuLimit] = useState<number>(2000);
	const [parallelism, setParallelism] = useState<number>(1);
	const [completions, setCompletions] = useState<number>(1);

	const [progressData, setProgressData] = useState<SimulationProgress[]>();
	const [scenarioOptions, setScenarioOptions] = useState<{ value: string; label: string }[]>([]);
	const [isFetchingProgress, setIsFetchingProgress] = useState(false);
	const [statusFilter, setStatusFilter] = useState<string | null>(null);
	const [filterOpen, setFilterOpen] = useState(false);

	const filteredProgressData = statusFilter
		? progressData?.filter((sim) => sim.status.toLowerCase() === statusFilter)
		: progressData;

	const fetchProgress = async () => {
		setIsFetchingProgress(true);
		await makeSafeRequest('simulation/progress', 'GET', {
			onErrorMessage: 'Failed to fetch simulation progress. Please try again.',
			onSuccess: (data: SimulationProgressResponse) => {
				setProgressData(data.progress);
				setIsFetchingProgress(false);
			},
		});
	};

	const fetchScenarios = async () => {
		await makeSafeRequest('scenarios/all/', 'GET', {
			onErrorMessage: 'Failed to fetch scenarios. Please try again.',
			onSuccess: (data: ScenariosResponse) => {
				setScenarioOptions(
					data.scenarios.map((scenario) => ({
						value: String(scenario.id),
						label: `#${scenario.id} · ${scenario.name}${
							scenario.project_name ? ` — ${scenario.project_name}` : ''
						}`,
					}))
				);
			},
		});
	};

	const scheduleSimulation = async () => {
		if (!scenarioId) {
			notifications.show({
				title: 'Validation Error',
				message: 'Scenario ID is required to schedule a simulation.',
				color: 'red',
			});
			return;
		}

		const response = await makeSafeRequest('simulation/job', 'POST', {
			payload: {
				scenario_id: String(scenarioId),
				duration: Number(duration),
				java_mem_value: Number(Math.floor(0.9 * memValue)), // Example: 90% of total memory for Java
				java_mem_unit: String(memUnit[0].toLowerCase()), // Assuming memUnit is in format "Gi" or "Mi"
				mem_value: Number(memValue),
				mem_unit: String(memUnit),
				cpu_value: Number(cpuLimit),
				parallelism: Number(parallelism),
				completions: Number(completions),
			},
			onSuccessMessage: `Simulation for scenario ${scenarioId} scheduled successfully!`,
			onErrorMessage: `Failed to schedule simulation for scenario ${scenarioId}. Please try again.`,
		});
		console.log('Schedule Simulation Response:', response);
	};

	useEffect(() => {
		fetchScenarios();
	}, []);

	useEffect(() => {
		setIsFetchingProgress(true);
		fetchProgress().then(() => setIsFetchingProgress(false));

		const interval = setInterval(() => {
			fetchProgress();
		}, 10000); // Refresh every 10 seconds

		return () => clearInterval(interval);
	}, []);

	return (
		<Grid
			columns={2}
			style={{
				height: 'calc(100vh - var(--header-height) - var(--footer-height) - var(--content-padding) * 2 - var(--breadcrumbs-height))',
			}}
		>
			<Grid.Col span={1} style={{ borderRight: '1px solid gray' }}>
				<Flex
					direction="column"
					align="center"
					gap="md"
					style={{ height: '100%', width: '100%', padding: '2rem' }}
				>
					<h1 style={{ margin: 0, alignSelf: 'flex-start' }}>Scheduling</h1>
					<Select
						label="Scenario"
						value={scenarioId}
						onChange={(val) => setScenarioId(val ?? '')}
						data={scenarioOptions}
						placeholder="Select a scenario"
						nothingFound="No scenarios found"
						searchable
						clearable
						style={{ width: '100%' }}
						required
					/>
					<NumberInput
						label="Duration (h)"
						value={duration}
						onChange={(val) => setDuration(Number(val))}
						min={1}
						placeholder="Enter duration in hours"
						style={{ width: '100%' }}
					/>
					<Group spacing="sm" style={{ width: '100%' }}>
						<NumberInput
							label="Process memory limit"
							value={memValue}
							onChange={(val) => setMemValue(Number(val))}
							min={1}
							style={{ flex: 1 }}
						/>
						<Select
							label="Unit"
							value={memUnit}
							onChange={(val) => setMemUnit(val ?? 'Gi')}
							data={[
								{ value: 'Gi', label: 'GiB (Gi)' },
								{ value: 'Mi', label: 'MiB (Mi)' },
							]}
							style={{ flex: 0.5 }}
						/>
					</Group>
					<NumberInput
						label="CPU Limit"
						description="Set CPU limit in millicores (e.g., 500m for 0.5 CPU)"
						value={cpuLimit}
						onChange={(val) => setCpuLimit(Number(val))}
						min={1}
						max={16000}
						sx={{ '.mantine-InputWrapper-description': { opacity: 0.5 } }}
						style={{ width: '100%' }}
					/>
					<NumberInput
						label="Parallelism"
						value={parallelism}
						onChange={(val) => setParallelism(Number(val))}
						min={1}
						max={completions}
						style={{ width: '100%' }}
					/>

					<NumberInput
						label="Completions"
						value={completions}
						onChange={(val) => setCompletions(Number(val))}
						min={1}
						style={{ width: '100%' }}
					/>

					<Button onClick={scheduleSimulation} style={{ marginTop: '1rem' }}>
						Schedule Simulation
					</Button>
				</Flex>
			</Grid.Col>
			<Grid.Col span={1} style={{ height: '100%', overflow: 'hidden' }}>
				<Flex
					direction="column"
					align="center"
					style={{ height: '100%', width: '100%', padding: '2rem' }}
				>
					<Group
						position="apart"
						style={{ width: '100%', marginBottom: '1rem', flexGrow: 0 }}
					>
						<h1 style={{ margin: 0 }}>Progress</h1>
						<Flex style={{ alignItems: 'center', gap: '1rem' }}>
							{isFetchingProgress && <Loader size="md" right={0} />}
							<Popover
								opened={filterOpen}
								onClose={() => setFilterOpen(false)}
								position="bottom-end"
								withArrow
								shadow="md"
							>
								<Popover.Target>
									<Tooltip
										label={
											statusFilter
												? `Filter: ${statusFilter}`
												: 'Filter by status'
										}
									>
										<ActionIcon
											variant={statusFilter ? 'filled' : 'subtle'}
											color={statusFilter ? theme.primaryColor : 'gray'}
											size="lg"
											onClick={() => setFilterOpen((o) => !o)}
										>
											<IconFilter size={18} />
										</ActionIcon>
									</Tooltip>
								</Popover.Target>
								<Popover.Dropdown>
									<Stack spacing={6}>
										<Text size="xs" weight={600} color="dimmed">
											Filter by status
										</Text>
										<Chip.Group
											value={statusFilter ?? ''}
											onChange={(v) => {
												setStatusFilter(
													(Array.isArray(v) ? v[0] : v) || null
												);
												setFilterOpen(false);
											}}
										>
											<CustomChip value="">All</CustomChip>
											<CustomChip value="pending">Pending</CustomChip>
											<CustomChip value="running">Running</CustomChip>
											<CustomChip value="finished">Finished</CustomChip>
											<CustomChip value="failed">Failed</CustomChip>
										</Chip.Group>
									</Stack>
								</Popover.Dropdown>
							</Popover>

							<Button onClick={fetchProgress}>Refresh</Button>
						</Flex>
					</Group>
					<Box style={{ width: '100%', flexGrow: 1, minHeight: 0 }}>
						<ScrollArea style={{ width: '100%', height: '100%' }}>
							<Stack spacing="sm">
								{!filteredProgressData || filteredProgressData.length === 0 ? (
									<Text color="dimmed" align="center">
										{statusFilter
											? 'No simulations match the selected status.'
											: 'No simulation data available.'}
									</Text>
								) : (
									filteredProgressData.map((sim) => (
										<Flex
											key={
												sim.runnum + (sim.runnum ? '' : crypto.randomUUID())
											}
											direction="column"
											gap={4}
											style={{
												border: '1px solid #ccc',
												borderRadius: 8,
												padding: '0.75rem',
											}}
										>
											<Group position="apart">
												<Text className={classes.runTitle}>
													Run{' '}
													{sim.runnum ? sim.runnum : 'initializing...'}
												</Text>
												<Badge color={statusColor(sim.status)}>
													{sim.status}
												</Badge>
											</Group>
											<Progress
												value={sim.progress * 100}
												size="lg"
												color={theme.primaryColor}
												animate={sim.status.toLowerCase() === 'running'}
											/>
											<Text size="xs" color="dimmed" align="right">
												{Math.floor(sim.progress * 100)}%
											</Text>
										</Flex>
									))
								)}
							</Stack>
						</ScrollArea>
					</Box>
				</Flex>
			</Grid.Col>
		</Grid>
	);
};

const useStyles = createStyles((theme) => ({
	runTitle: {
		color: `${theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.light[2]} !important`,
		fontWeight: 'bold',
	},
}));

const CustomChip = styled(Chip)`
	.mantine-Chip-label {
		color: ${({ theme }) =>
			theme.colorScheme === 'dark' ? theme.colors.dark[2] : theme.colors.light[3]};
	}
`;

export default SimulationPage;
